import React, { useState, useEffect, useCallback } from 'react';

const API_BASE =
  (window._env_ && window._env_.REACT_APP_API_URL) ||
  process.env.REACT_APP_API_URL ||
  '/api';

// ─── PALETA ───────────────────────────────────────────────────────────────────
const C = {
  amarillo:  '#F5C200',
  rojo:      '#7A1010',
  rojoClaro: '#9B1515',
  crema:     '#F5F0E8',
  cremaDark: '#EDE8DF',
  blanco:    '#FFFFFF',
  textoOsc:  '#3D0808',
  textoSub:  '#A07050',
  borde:     '#E8D8B0',
  bordeOsc:  '#C8A850',
  verde:     '#1B6B2A',
  verdeBg:   '#E8F5E9',
  error:     '#C62828',
  errorBg:   '#FFEBEE',
  gris:      '#6B7280',
  grisBg:    '#F3F4F6',
  azul:      '#1D4ED8',
  azulBg:    '#EFF6FF',
  sombra:    '0 2px 8px rgba(0,0,0,0.08)',
  sombraFuerte: '0 8px 24px rgba(0,0,0,0.12)',
};

// ─── ESTILOS GLOBALES ────────────────────────────────────────────────────────
const adminCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; background: #F1F5F9; font-family: 'Inter', system-ui, sans-serif; color: #1E293B; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: #F1F5F9; }
  ::-webkit-scrollbar-thumb { background: ${C.bordeOsc}; border-radius: 3px; }
  input, select, textarea { outline: none; font-family: inherit; }
  button { font-family: inherit; cursor: pointer; }
  table { border-collapse: collapse; width: 100%; }
  th { text-align: left; }
  .admin-row:hover { background: #FFF8E1 !important; }
`;

// ─── API ──────────────────────────────────────────────────────────────────────
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const api = {
  get:    (p)    => fetch(`${API_BASE}${p}`, { headers: authHeader() }).then(r => r.json()),
  post:   (p, b) => fetch(`${API_BASE}${p}`, { method: 'POST',   headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(b) }).then(r => r.json()),
  put:    (p, b) => fetch(`${API_BASE}${p}`, { method: 'PUT',    headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(b) }).then(r => r.json()),
  patch:  (p, b) => fetch(`${API_BASE}${p}`, { method: 'PATCH',  headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(b) }).then(r => r.json()),
  del:    (p)    => fetch(`${API_BASE}${p}`, { method: 'DELETE', headers: authHeader() }).then(r => r.json()),
};

const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';

// ─── UI ───────────────────────────────────────────────────────────────────────
const inputSt = { width: '100%', border: `1.5px solid ${C.borde}`, borderRadius: 8, padding: '9px 12px', fontSize: 14, marginBottom: 12, background: C.blanco, color: C.textoOsc };
const labelSt = { fontSize: 11, color: C.textoSub, fontWeight: 700, letterSpacing: 1, marginBottom: 4, display: 'block' };

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: type === 'error' ? C.error : C.verde, color: '#fff', padding: '12px 20px', borderRadius: 10, fontWeight: 700, fontSize: 14, boxShadow: C.sombraFuerte, maxWidth: 320 }}>
      {msg}
    </div>
  );
}

function Modal({ title, onClose, children, width = 480 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0005', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.blanco, borderRadius: 16, padding: 28, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: C.sombraFuerte }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: C.rojo }}>{title}</h2>
          <button onClick={onClose} style={{ background: C.cremaDark, border: 'none', borderRadius: 8, padding: '6px 12px', color: C.textoSub, fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Btn({ children, onClick, color = C.rojo, outline = false, disabled = false, sm = false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? '#DDD' : outline ? 'transparent' : color,
      color: disabled ? '#999' : outline ? color : '#fff',
      border: `2px solid ${disabled ? '#DDD' : color}`,
      borderRadius: 8, padding: sm ? '6px 14px' : '10px 18px',
      fontWeight: 700, fontSize: sm ? 13 : 14,
      boxShadow: (!outline && !disabled) ? `0 2px 8px ${color}33` : 'none',
    }}>{children}</button>
  );
}

function Badge({ children, color = C.gris, bg }) {
  return <span style={{ background: bg || color + '18', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{children}</span>;
}

function ConfirmModal({ msg, onConfirm, onCancel }) {
  return (
    <Modal title="⚠️ Confirmar acción" onClose={onCancel} width={360}>
      <p style={{ color: C.textoOsc, marginBottom: 20, lineHeight: 1.5 }}>{msg}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn onClick={onCancel} outline color={C.gris} sm>Cancelar</Btn>
        <Btn onClick={onConfirm} color={C.error} sm>Eliminar</Btn>
      </div>
    </Modal>
  );
}

// ─── SECCIÓN HEADER ───────────────────────────────────────────────────────────
function SectionHeader({ title, count, onAdd, addLabel = '+ Nuevo' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 900, color: C.rojo }}>{title}</h2>
        {count !== undefined && <Badge color={C.rojo}>{count}</Badge>}
      </div>
      {onAdd && <Btn onClick={onAdd} sm>{addLabel}</Btn>}
    </div>
  );
}

// ─── TABLA BASE ───────────────────────────────────────────────────────────────
function Table({ headers, children, empty = 'Sin registros' }) {
  return (
    <div style={{ background: C.blanco, borderRadius: 12, border: `1px solid ${C.borde}`, overflow: 'hidden', boxShadow: C.sombra }}>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr style={{ background: C.rojo }}>
              {headers.map(h => (
                <th key={h} style={{ padding: '11px 16px', color: C.amarillo, fontWeight: 800, fontSize: 12, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {React.Children.count(children) === 0
              ? <tr><td colSpan={headers.length} style={{ padding: 32, textAlign: 'center', color: C.textoSub }}>{empty}</td></tr>
              : children}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const tdSt = { padding: '10px 16px', fontSize: 14, borderBottom: `1px solid ${C.borde}`, color: C.textoOsc };
const ActionBtns = ({ onEdit, onDelete }) => (
  <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
    <div style={{ display: 'flex', gap: 6 }}>
      {onEdit && <button onClick={onEdit} style={{ background: C.azulBg, color: C.azul, border: `1px solid ${C.azul}33`, borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>✏️ Editar</button>}
      {onDelete && <button onClick={onDelete} style={{ background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`, borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>🗑 Eliminar</button>}
    </div>
  </td>
);

// ─── TOTALIZADOR (Retiros / Ingresos de Socios) ───────────────────────────────
// Totales calculados sobre la lista visible. Se usa como respaldo si el backend
// todavía no devuelve `totales` (con más de 100 registros sumaría solo la página).
const calcTotales = (lista) => ({
  monto:         lista.reduce((s, r) => s + (r.monto || 0), 0),
  efectivo:      lista.filter(r => r.mediodepago !== 'transferencia').reduce((s, r) => s + (r.monto || 0), 0),
  transferencia: lista.filter(r => r.mediodepago === 'transferencia').reduce((s, r) => s + (r.monto || 0), 0),
});

// Resumen en tarjetas, mismo estilo que el detalle de Aperturas de Caja
function ResumenTotales({ cantidad, visibles, totales, singular, plural, signo, colorTotal }) {
  const cards = [
    { label: plural.toUpperCase(), value: cantidad, color: C.textoSub,
      sub: cantidad > visibles ? `mostrando ${visibles}` : null },
    { label: 'TOTAL',          value: `${signo}${fmt(totales.monto)}`, color: colorTotal },
    { label: '💵 EFECTIVO',     value: fmt(totales.efectivo),           color: C.verde },
    { label: '📱 TRANSFERENCIA', value: fmt(totales.transferencia),      color: C.azul },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: 16, marginBottom: 14, background: C.crema, borderRadius: 12, border: `1px solid ${C.borde}` }}>
      {cards.map(s => (
        <div key={s.label} style={{ background: C.blanco, borderRadius: 10, padding: '10px 14px', border: `1px solid ${s.color}33` }}>
          <div style={{ fontSize: 9, color: s.color, fontWeight: 800, letterSpacing: 1 }}>{s.label}</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.value}</div>
          {s.sub && <div style={{ fontSize: 10, color: C.textoSub, marginTop: 2 }}>{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── CRUD PRODUCTOS ───────────────────────────────────────────────────────────
function CRUDProductos({ showToast }) {
  const [items, setItems]     = useState([]);
  const [modal, setModal]     = useState(null); // null | 'nuevo' | item
  const [confirm, setConfirm] = useState(null);
  const [form, setForm]       = useState({ productonombre: '', productotipo: 'entero' });
  const [loading, setLoading] = useState(false);
  const [buscar, setBuscar]   = useState('');

  const load = useCallback(() => api.get('/productos').then(setItems), []);
  useEffect(() => { load(); }, [load]);

  const [categorias, setCategorias] = useState([]);
  useEffect(() => { api.get('/categorias').then(d => setCategorias(Array.isArray(d)?d:[])); }, []);

  const openNuevo  = () => { setForm({ productonombre: '', categoriaid: '' }); setModal('nuevo'); };
  const openEditar = (item) => { setForm({ productonombre: item.productonombre, categoriaid: item.categoriaid?._id || item.categoriaid || '' }); setModal(item); };

  const guardar = async () => {
    if (!form.productonombre.trim()) return showToast('Ingresar nombre', 'error');
    setLoading(true);
    try {
      const res = modal === 'nuevo'
        ? await api.post('/productos', form)
        : await api.put(`/productos/${modal._id}`, form);
      if (res._id) { showToast(modal === 'nuevo' ? 'Producto creado' : 'Producto actualizado'); setModal(null); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const eliminar = async (id) => {
    try {
      await api.del(`/productos/${id}`);
      showToast('Producto eliminado'); setConfirm(null); load();
    } catch { showToast('Error al eliminar', 'error'); }
  };

  const tipoColor = { entero: '#8B6914', trozo: C.rojo, elaborado: '#6A1B9A', menudencia: '#E65100', otro: C.gris };
  const filtrados = items.filter(i => i.productonombre.toLowerCase().includes(buscar.toLowerCase()));

  return (
    <div>
      <SectionHeader title="Productos" count={items.length} onAdd={openNuevo} />
      <input style={{ ...inputSt, marginBottom: 12, maxWidth: 300 }} placeholder="🔍 Buscar producto..." value={buscar} onChange={e => setBuscar(e.target.value)} />
      <Table headers={['Nombre', 'Categoría', 'Creado', 'Acciones']}>
        {filtrados.map((p, i) => (
          <tr key={p._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema }}>
            <td style={{ ...tdSt, fontWeight: 600 }}>{p.productonombre}</td>
            <td style={tdSt}><Badge color={C.rojo}>{p.categoriaid?.nombre || p.productotipo || '—'}</Badge></td>
            <td style={{ ...tdSt, color: C.textoSub, fontSize: 12 }}>{fmtDate(p.createdAt)}</td>
            <ActionBtns onEdit={() => openEditar(p)} onDelete={() => setConfirm(p)} />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '+ Nuevo Producto' : '✏️ Editar Producto'} onClose={() => setModal(null)}>
          <span style={labelSt}>NOMBRE</span>
          <input style={inputSt} value={form.productonombre} onChange={e => setForm(f => ({ ...f, productonombre: e.target.value }))} placeholder="Nombre del producto" autoFocus />
          <span style={labelSt}>CATEGORÍA</span>
          <select style={inputSt} value={form.categoriaid} onChange={e => setForm(f => ({ ...f, categoriaid: e.target.value }))}>
            <option value="">Sin categoría</option>
            {categorias.map(c => <option key={c._id} value={c._id}>{c.nombre}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading ? 'Guardando...' : 'Guardar'}</Btn>
          </div>
        </Modal>
      )}
      {confirm && <ConfirmModal msg={`¿Eliminar "${confirm.productonombre}"? Esta acción no se puede deshacer.`} onConfirm={() => eliminar(confirm._id)} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ─── CRUD PRECIOS ─────────────────────────────────────────────────────────────
function CRUDPrecios({ showToast }) {
  const [precios, setPrecios]     = useState([]);
  const [productos, setProductos] = useState([]);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState({ productoid: '', preciovta: '', preciocosto: '' });
  const [loading, setLoading]     = useState(false);
  const [buscar, setBuscar]       = useState('');

  const load = useCallback(() => {
    api.get('/precios/vigentes').then(setPrecios);
    api.get('/productos').then(setProductos);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNuevo = () => { setForm({ productoid: '', preciovta: '', preciocosto: '', publicarWeb: true }); setModal('nuevo'); };
  const openEditar = (p) => { setForm({ productoid: p.productoid?._id || '', preciovta: p.preciovta, preciocosto: p.preciocosto, publicarWeb: p.publicarWeb !== false }); setModal(p); };

  const ganancia = form.preciovta && form.preciocosto && +form.preciocosto > 0
    ? (((+form.preciovta - +form.preciocosto) / +form.preciocosto) * 100).toFixed(1) : null;

  const toggleWebAdmin = async (p) => {
    const res = await api.put(`/precios/${p._id}`, { publicarWeb: p.publicarWeb === false });
    if (res._id) { showToast(res.publicarWeb ? '✅ Publicado en dpollo.com.ar' : '🚫 Ocultado de la web'); load(); }
    else showToast(res.error || 'Error', 'error');
  };

  const guardar = async () => {
    if (!form.productoid || !form.preciovta || !form.preciocosto) return showToast('Completar todos los campos', 'error');
    setLoading(true);
    try {
      const res = await api.post('/precios', { ...form, preciovta: +form.preciovta, preciocosto: +form.preciocosto, publicarWeb: form.publicarWeb !== false });
      if (res._id) { showToast('Precio guardado'); setModal(null); setForm({ productoid: '', preciovta: '', preciocosto: '', publicarWeb: true }); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const filtrados = precios.filter(p => (p.productoid?.productonombre || '').toLowerCase().includes(buscar.toLowerCase()));

  return (
    <div>
      <SectionHeader title="Precios Vigentes" count={precios.length} onAdd={openNuevo} addLabel="+ Actualizar precio" />
      <input style={{ ...inputSt, marginBottom: 12, maxWidth: 300 }} placeholder="🔍 Buscar..." value={buscar} onChange={e => setBuscar(e.target.value)} />
      <Table headers={['Producto', 'Categoría', 'Precio Vta', 'Costo', 'Ganancia %', 'Web', 'Actualizado', 'Acción']}>
        {filtrados.map((p, i) => (
          <tr key={p._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema, opacity: p.publicarWeb === false ? 0.65 : 1 }}>
            <td style={{ ...tdSt, fontWeight: 600 }}>{p.productoid?.productonombre || '—'}</td>
            <td style={tdSt}>
              {(() => {
                const cat = p.productoid?.categoriaid;
                const color = (cat?.colorWeb && cat.colorWeb !== '') ? cat.colorWeb : C.rojo;
                const label = cat?.nombre || p.productoid?.productotipo || '—';
                return <Badge color={color}>{label}</Badge>;
              })()}
            </td>
            <td style={{ ...tdSt, fontWeight: 800, color: C.rojo }}>{fmt(p.preciovta)}</td>
            <td style={{ ...tdSt, color: C.textoSub }}>{fmt(p.preciocosto)}</td>
            <td style={tdSt}><Badge color={C.verde}>+{p.porcentajeganancia}%</Badge></td>
            <td style={tdSt}>
              <button onClick={() => toggleWebAdmin(p)} style={{ background: p.publicarWeb === false ? C.errorBg : C.verdeBg, color: p.publicarWeb === false ? C.error : C.verde, border: `1px solid ${p.publicarWeb === false ? C.error : C.verde}44`, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {p.publicarWeb === false ? '🚫 Oculto' : '🌐 Web'}
              </button>
            </td>
            <td style={{ ...tdSt, color: C.textoSub, fontSize: 12 }}>{fmtDate(p.updatedAt)}</td>
            <ActionBtns onEdit={() => openEditar(p)} />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '+ Nuevo Precio' : '✏️ Editar Precio'} onClose={() => setModal(null)}>
          <span style={labelSt}>PRODUCTO</span>
          <select style={inputSt} value={form.productoid} onChange={e => setForm(f => ({ ...f, productoid: e.target.value }))}>
            <option value="">Seleccionar producto...</option>
            {productos.map(p => <option key={p._id} value={p._id}>{p.productonombre}</option>)}
          </select>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={labelSt}>PRECIO VENTA ($)</span>
              <input style={inputSt} type="number" value={form.preciovta} onChange={e => setForm(f => ({ ...f, preciovta: e.target.value }))} placeholder="0" />
            </div>
            <div>
              <span style={labelSt}>COSTO ($)</span>
              <input style={inputSt} type="number" value={form.preciocosto} onChange={e => setForm(f => ({ ...f, preciocosto: e.target.value }))} placeholder="0" />
            </div>
          </div>
          {ganancia !== null && (
            <div style={{ padding: '10px 14px', background: C.verdeBg, borderRadius: 8, marginBottom: 12 }}>
              <span style={{ color: C.verde, fontWeight: 800 }}>Ganancia estimada: {ganancia}%</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', background: '#FFF9E6', borderRadius: 8, border: '1px solid #C8A85044' }}>
            <input type="checkbox" id="admin-pw" checked={form.publicarWeb !== false} onChange={e => setForm(f => ({ ...f, publicarWeb: e.target.checked }))} style={{ width: 18, height: 18, cursor: 'pointer' }}/>
            <label htmlFor="admin-pw" style={{ fontSize: 14, fontWeight: 700, color: '#3D0808', cursor: 'pointer' }}>🌐 Publicar en dpollo.com.ar</label>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading ? 'Guardando...' : 'Guardar'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── CRUD VENTAS ──────────────────────────────────────────────────────────────
function CRUDVentas({ showToast }) {
  const [ventas, setVentas]   = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [buscar, setBuscar]   = useState('');
  const [desde, setDesde]     = useState('');
  const [hasta, setHasta]     = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    let url = '/ventas?limit=200';
    if (desde) url += `&desde=${desde}`;
    if (hasta) url += `&hasta=${hasta}`;
    api.get(url).then(d => { setVentas(d.ventas || []); setLoading(false); });
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const anular = async (v) => {
    try {
      const res = await api.patch(`/ventas/${v._id}/anular`, {});
      if (res.ok) showToast('Venta anulada y quitada de caja');
      else showToast(res.error || 'Error al anular', 'error');
      setConfirm(null); load();
    } catch { showToast('Error al anular', 'error'); }
  };

  const filtradas = ventas.filter(v =>
    v.detalles?.some(d => d.productonombre?.toLowerCase().includes(buscar.toLowerCase())) ||
    (v.usuario || '').toLowerCase().includes(buscar.toLowerCase()) ||
    (v.observaciones || '').toLowerCase().includes(buscar.toLowerCase())
  );
  const totalFiltrado = filtradas.reduce((s, v) => s + v.total, 0);

  return (
    <div>
      <SectionHeader title="Ventas" count={filtradas.length} />
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input style={{ ...inputSt, marginBottom: 0, maxWidth: 220 }} placeholder="🔍 Buscar..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <Btn onClick={() => { setDesde(''); setHasta(''); setBuscar(''); }} outline color={C.gris} sm>Limpiar</Btn>
        <div style={{ marginLeft: 'auto', background: C.verdeBg, border: `1px solid ${C.verde}44`, borderRadius: 8, padding: '6px 14px', fontWeight: 800, color: C.verde, fontSize: 14 }}>
          Total: {fmt(totalFiltrado)}
        </div>
      </div>

      <Table headers={['Fecha', 'Productos', 'Obs.', 'Usuario', 'Total', 'Acciones']} empty="Sin ventas">
        {loading
          ? <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: C.textoSub }}>Cargando...</td></tr>
          : filtradas.map((v, i) => (
            <tr key={v._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema }}>
              <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(v.fecha)}</td>
              <td style={tdSt}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {v.detalles?.map((d, j) => (
                    <span key={j} style={{ background: '#FFF8E1', color: C.rojo, border: `1px solid ${C.amarillo}`, borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
                      {d.productonombre} ×{d.cantidad}
                    </span>
                  ))}
                </div>
              </td>
              <td style={{ ...tdSt, color: C.textoSub, fontSize: 12 }}>{v.observaciones || '—'}</td>
              <td style={{ ...tdSt, fontSize: 12, color: C.textoSub }}>👤 {v.usuario}</td>
              <td style={{ ...tdSt, whiteSpace: 'nowrap', textAlign: 'right' }}>
                <div style={{ fontWeight: 800, color: C.verde }}>{fmt(v.total)}</div>
                {v.detalles?.some(d => d.modificado) && (
                  <div style={{ fontSize: 10, color: '#C8A800', fontWeight: 700 }}>
                    calc: {fmt(v.detalles.reduce((s, d) => s + (d.totalCalculado || d.subtotal || 0), 0))}
                  </div>
                )}
              </td>
              <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setDetalle(v)} style={{ background: C.azulBg, color: C.azul, border: `1px solid ${C.azul}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>🔍 Ver</button>
                  <button onClick={() => setConfirm(v)} style={{ background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>🚫 Anular</button>
                </div>
              </td>
            </tr>
          ))
        }
      </Table>

      {detalle && (
        <Modal title="🔍 Detalle de Venta" onClose={() => setDetalle(null)} width={540}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div style={{ background: C.crema, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 11, color: C.textoSub, fontWeight: 700 }}>FECHA</div>
              <div style={{ fontWeight: 600 }}>{fmtDate(detalle.fecha)}</div>
            </div>
            <div style={{ background: C.crema, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 11, color: C.textoSub, fontWeight: 700 }}>USUARIO</div>
              <div style={{ fontWeight: 600 }}>{detalle.usuario}</div>
            </div>
          </div>
          <table style={{ marginBottom: 16 }}>
            <thead>
              <tr style={{ background: C.crema }}>
                {['Producto', 'Precio', 'Cantidad', 'Subtotal'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', fontSize: 11, color: C.textoSub, fontWeight: 700, borderBottom: `1px solid ${C.borde}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detalle.detalles?.map((d, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.borde}` }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{d.productonombre}</td>
                  <td style={{ padding: '8px 12px', color: C.textoSub }}>{fmt(d.preciovta)}</td>
                  <td style={{ padding: '8px 12px' }}>{d.cantidad}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: C.verde }}>{fmt(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: '#FFF8E1', borderRadius: 10 }}>
            <span style={{ fontWeight: 800, color: C.rojo }}>TOTAL</span>
            <span style={{ fontWeight: 900, fontSize: 20, color: C.rojo }}>{fmt(detalle.total)}</span>
          </div>
          {detalle.observaciones && <div style={{ marginTop: 10, padding: '8px 12px', background: C.crema, borderRadius: 8, fontSize: 13, color: C.textoSub }}>📝 {detalle.observaciones}</div>}
        </Modal>
      )}
      {confirm && <ConfirmModal msg={`¿Anular venta de ${fmt(confirm.total)} del ${fmtDate(confirm.fecha)}? Se elimina su ingreso en caja y, si esa caja ya estaba cerrada, se recalcula su saldo de cierre.`} onConfirm={() => anular(confirm)} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ─── CRUD COMPRAS ─────────────────────────────────────────────────────────────
function CRUDCompras({ showToast }) {
  const [compras, setCompras] = useState([]);
  const [modal, setModal]     = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm]       = useState({ monto: '', proveedor: '', descripcion: '', mediodepago: 'efectivo' });
  const [loading, setLoading] = useState(false);
  const [buscar, setBuscar]   = useState('');
  const [desde, setDesde]     = useState('');
  const [hasta, setHasta]     = useState('');

  const load = useCallback(() => {
    let url = '/compras';
    if (desde) url += `?desde=${desde}`;
    if (hasta) url += `${desde ? '&' : '?'}hasta=${hasta}`;
    api.get(url).then(d => setCompras(Array.isArray(d) ? d.sort((a,b) => new Date(b.fecha)-new Date(a.fecha)) : []));
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const openNuevo = () => { setForm({ monto: '', proveedor: '', descripcion: '', mediodepago: 'efectivo' }); setModal('nuevo'); };
  const openEditar = (c) => { setForm({ monto: c.monto, proveedor: c.proveedor || '', descripcion: c.descripcion || '', mediodepago: c.mediodepago || 'efectivo' }); setModal(c); };

  const guardar = async () => {
    if (!form.monto) return showToast('Ingresar monto', 'error');
    setLoading(true);
    try {
      const res = modal === 'nuevo'
        ? await api.post('/compras', { ...form, monto: +form.monto })
        : await api.put(`/compras/${modal._id}`, { ...form, monto: +form.monto });
      if (res._id) { showToast(modal === 'nuevo' ? 'Compra registrada' : 'Compra actualizada'); setModal(null); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const anular = async (c) => {
    try {
      const res = await api.patch(`/compras/${c._id}/anular`, {});
      if (res.ok) showToast('Compra anulada y quitada de caja');
      else showToast(res.error || 'Error al anular', 'error');
      setConfirm(null); load();
    } catch { showToast('Error al anular', 'error'); }
  };

  const filtradas = compras.filter(c =>
    (c.proveedor || '').toLowerCase().includes(buscar.toLowerCase()) ||
    (c.descripcion || '').toLowerCase().includes(buscar.toLowerCase())
  );
  const total = filtradas.reduce((s, c) => s + c.monto, 0);

  return (
    <div>
      <SectionHeader title="Compras" count={filtradas.length} onAdd={openNuevo} addLabel="+ Nueva compra" />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input style={{ ...inputSt, marginBottom: 0, maxWidth: 220 }} placeholder="🔍 Buscar proveedor..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <Btn onClick={() => { setDesde(''); setHasta(''); setBuscar(''); }} outline color={C.gris} sm>Limpiar</Btn>
        <div style={{ marginLeft: 'auto', background: C.errorBg, border: `1px solid ${C.error}44`, borderRadius: 8, padding: '6px 14px', fontWeight: 800, color: C.error, fontSize: 14 }}>
          Total: {fmt(total)}
        </div>
      </div>

      <Table headers={['Fecha', 'Proveedor', 'Descripción', 'Monto', 'Acciones']}>
        {filtradas.map((c, i) => (
          <tr key={c._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema, opacity: c.anulada ? 0.5 : 1 }}>
            <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(c.fecha)}</td>
            <td style={{ ...tdSt, fontWeight: 600 }}>{c.proveedor || '—'} {c.anulada && <Badge color={C.error}>ANULADA</Badge>}</td>
            <td style={{ ...tdSt, color: C.textoSub }}>{c.descripcion || '—'}</td>
            <td style={{ ...tdSt, fontWeight: 800, color: C.error, whiteSpace: 'nowrap' }}>{fmt(c.monto)}</td>
            <ActionBtns
              onEdit={!c.anulada ? () => openEditar(c) : undefined}
              onDelete={!c.anulada ? () => setConfirm(c) : undefined}
            />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '+ Nueva Compra' : '✏️ Editar Compra'} onClose={() => setModal(null)}>
          <span style={labelSt}>MONTO ($)</span>
          <input style={inputSt} type="number" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} placeholder="0" autoFocus />
          <span style={labelSt}>PROVEEDOR</span>
          <input style={inputSt} value={form.proveedor} onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} placeholder="Nombre del proveedor" />
          <span style={labelSt}>DESCRIPCIÓN</span>
          <input style={inputSt} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Detalle de la compra" />
          <span style={labelSt}>MEDIO DE PAGO</span>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['efectivo','💵 Efectivo',C.verde],['transferencia','📱 Transferencia',C.azul]].map(([v,l,c]) => (
              <button key={v} onClick={() => setForm(f => ({ ...f, mediodepago: v }))} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${form.mediodepago === v ? c : C.borde}`, background: form.mediodepago === v ? c+'18' : C.blanco, color: form.mediodepago === v ? c : C.textoSub, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>
          {modal !== 'nuevo' && (
            <div style={{ fontSize: 11, color: C.textoSub, marginTop: -6, marginBottom: 12 }}>Los cambios de monto y medio de pago se aplican también al movimiento en caja.</div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading ? 'Guardando...' : 'Guardar'}</Btn>
          </div>
        </Modal>
      )}
      {confirm && <ConfirmModal msg={`¿Anular compra de ${fmt(confirm.monto)} a "${confirm.proveedor || 'sin proveedor'}"? Se elimina su egreso en caja y, si esa caja ya estaba cerrada, se recalcula su saldo de cierre.`} onConfirm={() => anular(confirm)} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ─── CRUD USUARIOS ────────────────────────────────────────────────────────────
function CRUDUsuarios({ showToast }) {
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState({ nombre: '', username: '', password: '', rol: 'vendedor', categoria: 'vendedor' });
  const [loading, setLoading]   = useState(false);

  const load = useCallback(() => api.get('/auth/usuarios').then(d => Array.isArray(d) ? setUsuarios(d) : null), []);
  useEffect(() => { load(); }, [load]);

  const openNuevo = () => { setForm({ nombre: '', username: '', password: '', rol: 'vendedor', categoria: 'vendedor' }); setModal('nuevo'); };
  const openEditar = (u) => { setForm({ nombre: u.nombre, username: u.username, password: '', rol: u.rol, categoria: u.categoria || 'vendedor' }); setModal(u); };

  const guardar = async () => {
    if (!form.nombre || !form.username) return showToast('Completar nombre y usuario', 'error');
    if (modal === 'nuevo' && !form.password) return showToast('Ingresar contraseña', 'error');
    setLoading(true);
    try {
      const body = { nombre: form.nombre, username: form.username, rol: form.rol, categoria: form.categoria };
      if (form.password) body.password = form.password;
      const res = modal === 'nuevo'
        ? await api.post('/auth/register', body)
        : await api.put(`/auth/usuarios/${modal._id}`, body);
      if (res._id || res.id) { showToast(modal === 'nuevo' ? 'Usuario creado' : 'Usuario actualizado'); setModal(null); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const toggleActivo = async (u) => {
    try {
      await api.put(`/auth/usuarios/${u._id}`, { activo: !u.activo });
      showToast(u.activo ? 'Usuario desactivado' : 'Usuario activado'); load();
    } catch { showToast('Error', 'error'); }
  };

  return (
    <div>
      <SectionHeader title="Usuarios" count={usuarios.length} onAdd={openNuevo} addLabel="+ Nuevo usuario" />
      <Table headers={['Nombre', 'Usuario', 'Rol', 'Categoría', 'Estado', 'Creado', 'Acciones']}>
        {usuarios.map((u, i) => (
          <tr key={u._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema, opacity: u.activo ? 1 : 0.6 }}>
            <td style={{ ...tdSt, fontWeight: 600 }}>{u.nombre}</td>
            <td style={{ ...tdSt, fontFamily: 'monospace', fontSize: 13 }}>@{u.username}</td>
            <td style={tdSt}><Badge color={u.rol === 'admin' ? C.rojo : C.azul}>{u.rol}</Badge></td>
            <td style={tdSt}>{u.categoria === 'socio' ? <Badge color={C.verde}>🤝 Socio</Badge> : <span style={{ color: C.textoSub, fontSize: 12 }}>—</span>}</td>
            <td style={tdSt}>
              <button onClick={() => toggleActivo(u)} style={{ background: u.activo ? C.verdeBg : C.errorBg, color: u.activo ? C.verde : C.error, border: `1px solid ${u.activo ? C.verde : C.error}44`, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
                {u.activo ? '✓ Activo' : '✗ Inactivo'}
              </button>
            </td>
            <td style={{ ...tdSt, color: C.textoSub, fontSize: 12 }}>{fmtDate(u.createdAt)}</td>
            <ActionBtns onEdit={() => openEditar(u)} />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '+ Nuevo Usuario' : '✏️ Editar Usuario'} onClose={() => setModal(null)}>
          <span style={labelSt}>NOMBRE COMPLETO</span>
          <input style={inputSt} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre y apellido" autoFocus />
          <span style={labelSt}>NOMBRE DE USUARIO</span>
          <input style={inputSt} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="username" autoComplete="off" />
          <span style={labelSt}>{modal === 'nuevo' ? 'CONTRASEÑA' : 'NUEVA CONTRASEÑA (dejar vacío para no cambiar)'}</span>
          <input style={inputSt} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" autoComplete="new-password" />
          <span style={labelSt}>ROL</span>
          <select style={inputSt} value={form.rol} onChange={e => setForm(f => ({ ...f, rol: e.target.value }))}>
            <option value="vendedor">Vendedor</option>
            <option value="admin">Administrador</option>
          </select>
          <span style={labelSt}>CATEGORÍA</span>
          <select style={inputSt} value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
            <option value="vendedor">Vendedor</option>
            <option value="socio">🤝 Socio</option>
          </select>
          <div style={{ fontSize: 11, color: C.textoSub, marginTop: -8, marginBottom: 12 }}>Marcá "Socio" para que aparezca como opción en Retiros de Socios.</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading ? 'Guardando...' : 'Guardar'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── CRUD CAJA ────────────────────────────────────────────────────────────────
// Alta, edición y baja de movimientos. Los movimientos vinculados a una venta,
// compra, retiro o ingreso de socio solo permiten editar la descripción: su monto
// se corrige anulando el registro de origen, para no desincronizar los datos.
const hoyAR = () => new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString().split('T')[0];
const partesAR = (d) => {
  const x = new Date(new Date(d).getTime() - 3 * 60 * 60 * 1000).toISOString();
  return { fecha: x.slice(0, 10), hora: x.slice(11, 16) };
};
const esVinculado = (m) => !!m.movimientoid || ['venta', 'compra'].includes(m.tiporef);
const movFormVacio = () => {
  const p = partesAR(new Date());
  return { tipomovimiento: 'ingreso', mediodepago: 'efectivo', descmovimiento: '', monto: '', fecha: p.fecha, hora: p.hora, aperturaid: '' };
};

function CRUDCaja({ showToast }) {
  const [movs, setMovs]           = useState([]);
  const [saldo, setSaldo]         = useState(0);
  const [aperturas, setAperturas] = useState([]);
  const [modal, setModal]         = useState(null); // null | 'nuevo' | movimiento
  const [form, setForm]           = useState(movFormVacio);
  const [confirm, setConfirm]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [desde, setDesde]         = useState(hoyAR());
  const [hasta, setHasta]         = useState(hoyAR());
  const [buscar, setBuscar]       = useState('');

  const load = useCallback(() => {
    api.get(`/caja?desde=${desde}&hasta=${hasta}`).then(d => setMovs(Array.isArray(d) ? d.sort((a,b) => new Date(b.fecha)-new Date(a.fecha)) : []));
    api.get('/caja/saldo').then(d => setSaldo(d.saldo || 0));
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/caja/aperturas?limit=60').then(d => setAperturas(Array.isArray(d) ? d : []));
  }, []);

  const abrirNuevo = () => { setForm(movFormVacio()); setModal('nuevo'); };
  const abrirEditar = (m) => {
    const p = partesAR(m.fecha);
    setForm({
      tipomovimiento: m.tipomovimiento, mediodepago: m.mediodepago || 'efectivo',
      descmovimiento: m.descmovimiento || '', monto: m.monto,
      fecha: p.fecha, hora: p.hora,
      aperturaid: m.aperturaid?._id || m.aperturaid || '',
    });
    setModal(m);
  };

  const vinculado = modal && modal !== 'nuevo' && esVinculado(modal);

  const guardar = async () => {
    if (!form.descmovimiento.trim()) return showToast('Ingresar una descripción', 'error');
    if (!vinculado && (!form.monto || +form.monto <= 0)) return showToast('Ingresar un monto válido', 'error');
    if (!vinculado && (!form.fecha || !form.hora)) return showToast('Completar fecha y hora', 'error');
    setLoading(true);
    try {
      const body = vinculado
        ? { descmovimiento: form.descmovimiento.trim() }
        : {
            tipomovimiento: form.tipomovimiento,
            mediodepago:    form.mediodepago,
            descmovimiento: form.descmovimiento.trim(),
            monto:          +form.monto,
            fecha:          new Date(`${form.fecha}T${form.hora}:00-03:00`).toISOString(), // hora argentina explícita
            aperturaid:     form.aperturaid || undefined,
          };
      const res = modal === 'nuevo'
        ? await api.post('/caja-admin/movimientos', body)
        : await api.put(`/caja-admin/movimientos/${modal._id}`, body);
      if (res._id) { showToast(modal === 'nuevo' ? 'Movimiento registrado' : 'Movimiento actualizado'); setModal(null); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const eliminar = async (m) => {
    try {
      const res = await api.del(`/caja-admin/movimientos/${m._id}`);
      if (res.ok) { showToast('Movimiento eliminado'); load(); }
      else showToast(res.error || 'Error al eliminar', 'error');
    } catch { showToast('Error al eliminar', 'error'); }
    setConfirm(null);
  };

  const filtrados = buscar
    ? movs.filter(m => (m.descmovimiento || '').toLowerCase().includes(buscar.toLowerCase()))
    : movs;
  const ingresos = filtrados.filter(m => m.tipomovimiento === 'ingreso').reduce((s, m) => s + m.monto, 0);
  const egresos  = filtrados.filter(m => m.tipomovimiento === 'egreso').reduce((s, m) => s + m.monto, 0);

  const toggleSt = (activo, color) => ({ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${activo ? color : C.borde}`, background: activo ? color + '18' : C.blanco, color: activo ? color : C.textoSub, fontWeight: 700, fontSize: 13, cursor: 'pointer' });

  return (
    <div>
      <SectionHeader title="Movimientos de Caja" count={filtrados.length} onAdd={abrirNuevo} addLabel="+ Movimiento manual" />

      {/* Stats: se recalculan con cada alta, edición o baja */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'SALDO TOTAL', value: fmt(saldo), color: C.rojo, bg: '#FFF8E1' },
          { label: 'INGRESOS (período)', value: fmt(ingresos), color: C.verde, bg: C.verdeBg },
          { label: 'EGRESOS (período)', value: fmt(egresos), color: C.error, bg: C.errorBg },
          { label: 'NETO (período)', value: fmt(ingresos - egresos), color: ingresos >= egresos ? C.verde : C.error, bg: C.crema },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: 14, border: `1.5px solid ${s.color}33` }}>
            <div style={{ fontSize: 10, color: s.color, fontWeight: 800, letterSpacing: 1 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input style={{ ...inputSt, marginBottom: 0, maxWidth: 220 }} placeholder="🔍 Buscar por descripción..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <Btn onClick={() => { setDesde(hoyAR()); setHasta(hoyAR()); setBuscar(''); }} outline color={C.gris} sm>Limpiar</Btn>
      </div>

      <Table headers={['Fecha', 'Tipo', 'M. Pago', 'Descripción', 'Referencia', 'Monto', 'Acciones']} empty={buscar ? 'Sin resultados para la búsqueda' : 'Sin registros'}>
        {filtrados.map((m, i) => (
          <tr key={m._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema }}>
            <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(m.fecha)}</td>
            <td style={tdSt}><Badge color={m.tipomovimiento === 'ingreso' ? C.verde : C.error}>{m.tipomovimiento === 'ingreso' ? '↑ Ingreso' : '↓ Egreso'}</Badge></td>
            <td style={tdSt}>
              <span style={{ background: m.mediodepago === 'transferencia' ? C.azulBg : C.verdeBg, color: m.mediodepago === 'transferencia' ? C.azul : C.verde, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
                {m.mediodepago === 'transferencia' ? '📱 Trans.' : '💵 Efect.'}
              </span>
            </td>
            <td style={tdSt}>{m.descmovimiento}</td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub, whiteSpace: 'nowrap' }}>
              {m.tiporef || '—'}{esVinculado(m) && <span title="Vinculado a un registro de origen"> 🔗</span>}
            </td>
            <td style={{ ...tdSt, fontWeight: 800, color: m.tipomovimiento === 'ingreso' ? C.verde : C.error, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {m.tipomovimiento === 'egreso' ? '−' : '+'}{fmt(m.monto)}
            </td>
            <ActionBtns onEdit={() => abrirEditar(m)} onDelete={!esVinculado(m) ? () => setConfirm(m) : undefined} />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '💰 Nuevo movimiento de caja' : '✏️ Editar movimiento'} onClose={() => setModal(null)}>
          {vinculado ? (
            <>
              <div style={{ background: C.azulBg, border: `1px solid ${C.azul}33`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: C.azul }}>
                🔗 Este movimiento viene de un registro de <b>{modal.tiporef || 'origen'}</b>. Solo podés cambiar la descripción. Para corregir el monto, editá la compra en <b>Compras</b>, o anulá la venta en <b>Ventas</b> (se quita de caja sola) y cargala de nuevo.
              </div>
              <div style={{ fontSize: 12, color: C.textoSub, marginBottom: 12 }}>
                {fmtDate(modal.fecha)} · <Badge color={modal.tipomovimiento === 'ingreso' ? C.verde : C.error}>{modal.tipomovimiento === 'ingreso' ? '↑ Ingreso' : '↓ Egreso'}</Badge> · {fmt(modal.monto)}
              </div>
            </>
          ) : (
            <>
              <span style={labelSt}>TIPO</span>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {[['ingreso', '↑ Ingreso', C.verde], ['egreso', '↓ Egreso', C.error]].map(([v, l, c]) => (
                  <button key={v} onClick={() => setForm(f => ({ ...f, tipomovimiento: v }))} style={toggleSt(form.tipomovimiento === v, c)}>{l}</button>
                ))}
              </div>
              <span style={labelSt}>MEDIO DE PAGO</span>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {[['efectivo', '💵 Efectivo', C.verde], ['transferencia', '📱 Transferencia', C.azul]].map(([v, l, c]) => (
                  <button key={v} onClick={() => setForm(f => ({ ...f, mediodepago: v }))} style={toggleSt(form.mediodepago === v, c)}>{l}</button>
                ))}
              </div>
            </>
          )}

          <span style={labelSt}>DESCRIPCIÓN</span>
          <input style={inputSt} value={form.descmovimiento} onChange={e => setForm(f => ({ ...f, descmovimiento: e.target.value }))} placeholder="Motivo del movimiento" autoFocus />

          {!vinculado && (
            <>
              <span style={labelSt}>MONTO ($)</span>
              <input style={inputSt} type="number" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} placeholder="0" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={labelSt}>FECHA</span>
                  <input type="date" style={inputSt} value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div>
                  <span style={labelSt}>HORA</span>
                  <input type="time" style={inputSt} value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))} />
                </div>
              </div>
              <span style={labelSt}>CAJA A IMPACTAR</span>
              <select style={inputSt} value={form.aperturaid} onChange={e => setForm(f => ({ ...f, aperturaid: e.target.value }))}>
                <option value="">Caja del día de la fecha (automática)</option>
                {aperturas.map(a => {
                  const fechaStr = new Date(a.fecha).toLocaleDateString('es-AR', { timeZone: 'UTC' });
                  const horaStr  = new Date(a.fechaApertura).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
                  return <option key={a._id} value={a._id}>{fechaStr} — {horaStr} ({a.estado}) ${(a.saldoInicial||0).toLocaleString()}</option>;
                })}
              </select>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading ? 'Guardando...' : modal === 'nuevo' ? 'Registrar' : 'Guardar cambios'}</Btn>
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          msg={`¿Eliminar el ${confirm.tipomovimiento} de ${fmt(confirm.monto)} "${confirm.descmovimiento}"? Si la caja de ese día está cerrada, se recalcula su saldo de cierre.`}
          onConfirm={() => eliminar(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── PANEL ADMIN PRINCIPAL ────────────────────────────────────────────────────

// ─── CRUD CATEGORIAS ──────────────────────────────────────────────────────────
function CRUDCategorias({ showToast }) {
  const [cats, setCats]     = useState([]);
  const [modal, setModal]   = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm]     = useState({ slug:'', nombre:'', descripcion:'', orden:1, tipos:[], visibleWeb:true, colorWeb:'' });
  const [loading, setLoading] = useState(false);

  const TIPOS = ['entero','trozo','elaborado','menudencia','otro'];
  const load  = useCallback(() => api.get('/categorias').then(d => setCats(Array.isArray(d)?d:[])), []);
  useEffect(() => { load(); }, [load]);

  const openNuevo  = () => { setForm({ slug:'', nombre:'', descripcion:'', orden:cats.length+1, tipos:[], visibleWeb:true, colorWeb:'' }); setModal('nuevo'); };
  const openEditar = (c) => { setForm({ slug:c.slug, nombre:c.nombre, descripcion:c.descripcion||'', orden:c.orden, tipos:c.tipos||[], visibleWeb:c.visibleWeb, colorWeb:c.colorWeb||'' }); setModal(c); };

  const toggleTipo = (t) => setForm(f => ({ ...f, tipos: f.tipos.includes(t) ? f.tipos.filter(x=>x!==t) : [...f.tipos, t] }));

  const guardar = async () => {
    if (!form.slug || !form.nombre) return showToast('Completar slug y nombre', 'error');
    setLoading(true);
    try {
      const res = modal === 'nuevo'
        ? await api.post('/categorias', form)
        : await api.put(`/categorias/${modal._id}`, form);
      if (res._id) { showToast(modal === 'nuevo' ? 'Categoría creada' : 'Categoría actualizada'); setModal(null); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const eliminar = async (c) => {
    try { await api.del(`/categorias/${c._id}`); showToast('Categoría eliminada'); setConfirm(null); load(); }
    catch { showToast('Error al eliminar', 'error'); }
  };

  return (
    <div>
      <SectionHeader title="Categorías Web" count={cats.length} onAdd={openNuevo} addLabel="+ Nueva categoría" />
      <div style={{ background: C.azulBg, border: `1px solid ${C.azul}33`, borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.azul }}>
        💡 Las categorías vinculan los tipos de PolloSystem con las secciones visibles en <b>dpollo.com.ar</b>. El endpoint público <code>/api/categorias/publico</code> las expone con sus productos y precios vigentes.
      </div>
      <Table headers={['Slug', 'Nombre', 'Tipos vinculados', 'Orden', 'Web', 'Acciones']}>
        {cats.map((c, i) => (
          <tr key={c._id} className="admin-row" style={{ background: i%2===0?C.blanco:C.crema }}>
            <td style={{ ...tdSt, fontFamily:'monospace', fontSize:13 }}>{c.slug}</td>
            <td style={{ ...tdSt, fontWeight:600 }}>{c.nombre}</td>
            <td style={tdSt}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {(c.tipos||[]).map(t => <Badge key={t} color={C.rojo}>{t}</Badge>)}
                {(!c.tipos||c.tipos.length===0) && <span style={{ color:C.textoSub, fontSize:12 }}>Sin tipos</span>}
              </div>
            </td>
            <td style={{ ...tdSt, textAlign:'center' }}>{c.orden}</td>
            <td style={tdSt}><Badge color={c.visibleWeb?C.verde:C.gris}>{c.visibleWeb?'✓ Visible':'Oculta'}</Badge></td>
            <ActionBtns onEdit={() => openEditar(c)} onDelete={() => setConfirm(c)} />
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title={modal === 'nuevo' ? '+ Nueva Categoría' : '✏️ Editar Categoría'} onClose={() => setModal(null)}>
          <span style={labelSt}>SLUG (identificador único, sin espacios)</span>
          <input style={inputSt} value={form.slug} onChange={e=>setForm(f=>({...f,slug:e.target.value.toLowerCase().replace(/\s/g,'-')}))} placeholder="ej: fresco" disabled={modal !== 'nuevo'} autoFocus/>
          <span style={labelSt}>NOMBRE VISIBLE EN LA WEB</span>
          <input style={inputSt} value={form.nombre} onChange={e=>setForm(f=>({...f,nombre:e.target.value}))} placeholder="ej: Pollo Fresco"/>
          <span style={labelSt}>DESCRIPCIÓN (opcional)</span>
          <input style={inputSt} value={form.descripcion} onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))} placeholder="Descripción breve"/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <span style={labelSt}>ORDEN</span>
              <input style={inputSt} type="number" value={form.orden} onChange={e=>setForm(f=>({...f,orden:+e.target.value}))} min="1"/>
            </div>
            <div>
              <span style={labelSt}>COLOR WEB (hex, opcional)</span>
              <input style={inputSt} value={form.colorWeb} onChange={e=>setForm(f=>({...f,colorWeb:e.target.value}))} placeholder="#F5C200"/>
            </div>
          </div>
          <span style={labelSt}>TIPOS DE POLLOSYSTEM QUE INCLUYE</span>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
            {['entero','trozo','elaborado','menudencia','otro'].map(t => (
              <button key={t} onClick={() => toggleTipo(t)} style={{ background:form.tipos.includes(t)?C.rojo:C.blanco, color:form.tipos.includes(t)?'#fff':C.textoSub, border:`2px solid ${form.tipos.includes(t)?C.rojo:C.borde}`, borderRadius:20, padding:'6px 14px', fontSize:13, fontWeight:700 }}>{t}</button>
            ))}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
            <input type="checkbox" id="visibleWeb" checked={form.visibleWeb} onChange={e=>setForm(f=>({...f,visibleWeb:e.target.checked}))} style={{ width:18, height:18 }}/>
            <label htmlFor="visibleWeb" style={{ fontSize:14, fontWeight:600, color:C.textoOsc }}>Visible en dpollo.com.ar</label>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Btn onClick={() => setModal(null)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={guardar} disabled={loading} sm>{loading?'Guardando...':'Guardar'}</Btn>
          </div>
        </Modal>
      )}
      {confirm && <ConfirmModal msg={`¿Eliminar la categoría "${confirm.nombre}"?`} onConfirm={() => eliminar(confirm)} onCancel={() => setConfirm(null)} />}
    </div>
  );
}


// ─── CRUD APERTURAS DE CAJA ───────────────────────────────────────────────────
function CRUDAperturas({ showToast }) {
  const [aperturas, setAperturas] = useState([]);
  const [detalle,   setDetalle]   = useState(null); // apertura seleccionada con movimientos
  const [modalAb,   setModalAb]   = useState(false); // modal abrir caja
  const [confirm,   setConfirm]   = useState(null);
  const [formAb,    setFormAb]    = useState({ efectivoInicial: '', transferenciaInicial: '', observaciones: '' });
  const [loading,   setLoading]   = useState(false);
  const [desde,     setDesde]     = useState('');
  const [hasta,     setHasta]     = useState('');

  const load = useCallback(() => {
    let url = '/caja/aperturas?limit=50';
    if (desde) url += `&desde=${desde}`;
    if (hasta) url += `&hasta=${hasta}`;
    api.get(url).then(d => setAperturas(Array.isArray(d) ? d : []));
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const loadDetalle = async (ap) => {
    const movs = await api.get(`/caja/movimientos?aperturaid=${ap._id}`);
    setDetalle({ apertura: ap, movimientos: Array.isArray(movs) ? movs : [] });
  };

  const abrirCaja = async () => {
    const ef = +formAb.efectivoInicial || 0;
    const tr = +formAb.transferenciaInicial || 0;
    setLoading(true);
    const res = await api.post('/caja/apertura', {
      saldoInicial: ef + tr, efectivoInicial: ef,
      transferenciaInicial: tr, observaciones: formAb.observaciones
    });
    setLoading(false);
    if (res._id || res.apertura) {
      showToast('✅ Caja abierta');
      setModalAb(false);
      setFormAb({ efectivoInicial: '', transferenciaInicial: '', observaciones: '' });
      load();
    } else showToast(res.error || 'Error', 'error');
  };

  const cerrarCaja = async (ap) => {
    const res = await api.patch(`/caja/apertura/${ap._id}/cerrar`, { observaciones: 'Cierre manual desde admin' });
    if (res._id) { showToast('🔒 Caja cerrada'); load(); if (detalle?.apertura._id === ap._id) setDetalle(null); }
    else showToast(res.error || 'Error', 'error');
  };

  const reabrirCaja = async (ap) => {
    const res = await api.patch(`/caja/apertura/${ap._id}/reabrir`, {});
    if (res._id) { showToast('🔓 Caja reabierta'); load(); }
    else showToast(res.error || 'Error', 'error');
  };

  const eliminarApertura = async (ap) => {
    // Solo permite eliminar aperturas sin movimientos
    try {
      await api.del(`/caja/apertura/${ap._id}`);
      showToast('Apertura eliminada'); setConfirm(null); load();
      if (detalle?.apertura._id === ap._id) setDetalle(null);
    } catch { showToast('Error al eliminar', 'error'); }
  };

  const fmt2 = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '—';
  const fmtT = (d) => d ? new Date(d).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDT = (d) => d ? `${fmtD(d)} ${fmtT(d)}` : '—';

  return (
    <div>
      <SectionHeader title="Aperturas de Caja" count={aperturas.length} onAdd={() => setModalAb(true)} addLabel="+ Abrir Caja" />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <Btn onClick={() => { setDesde(''); setHasta(''); }} outline color={C.gris} sm>Limpiar</Btn>
      </div>

      {/* Tabla aperturas */}
      <Table headers={['Fecha', 'Estado', 'Apertura', 'Cierre', 'Saldo Inicial', '💵 Efectivo', '📱 Transf.', 'Saldo Final', 'Usuario', 'Acciones']}>
        {aperturas.length === 0 && (
          <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: C.textoSub }}>Sin aperturas registradas</td></tr>
        )}
        {aperturas.map((ap, i) => (
          <tr key={ap._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema }}>
            <td style={{ ...tdSt, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtD(ap.fecha)}</td>
            <td style={tdSt}>
              <Badge color={ap.estado === 'abierta' ? C.verde : C.gris}>
                {ap.estado === 'abierta' ? '🟢 Abierta' : '🔒 Cerrada'}
              </Badge>
            </td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub, whiteSpace: 'nowrap' }}>{fmtT(ap.fechaApertura)}</td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub, whiteSpace: 'nowrap' }}>{ap.fechaCierre ? fmtT(ap.fechaCierre) : '—'}</td>
            <td style={{ ...tdSt, fontWeight: 700 }}>{fmt2(ap.saldoInicial)}</td>
            <td style={{ ...tdSt, color: C.verde }}>{fmt2(ap.efectivoInicial)}</td>
            <td style={{ ...tdSt, color: C.azul }}>{fmt2(ap.transferenciaInicial)}</td>
            <td style={{ ...tdSt, fontWeight: 800, color: ap.saldoCierre != null ? C.rojo : C.textoSub }}>
              {ap.saldoCierre != null ? fmt2(ap.saldoCierre) : '—'}
            </td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub }}>{ap.usuario}</td>
            <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => loadDetalle(ap)} style={{ background: C.azulBg, color: C.azul, border: `1px solid ${C.azul}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                  📋 Movimientos
                </button>
                {ap.estado === 'abierta' && (
                  <button onClick={() => cerrarCaja(ap)} style={{ background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                    🔒 Cerrar
                  </button>
                )}
                {ap.estado === 'cerrada' && (
                  <>
                    <button onClick={() => reabrirCaja(ap)} style={{ background: '#FFF3CD', color: '#7A5900', border: '1px solid #C8A80044', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                      🔓 Reabrir
                    </button>
                    <button onClick={() => setConfirm(ap)} style={{ background: C.grisBg, color: C.gris, border: `1px solid ${C.gris}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                      🗑 Eliminar
                    </button>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>

      {/* Detalle de movimientos de una apertura */}
      {detalle && (
        <div style={{ marginTop: 24, background: C.blanco, borderRadius: 14, border: `2px solid ${C.borde}`, overflow: 'hidden', boxShadow: C.sombra }}>
          <div style={{ background: C.rojo, padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: C.amarillo, fontWeight: 900, fontSize: 15 }}>
              📋 Movimientos — {fmtD(detalle.apertura.fecha)}
              <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: '#fff9' }}>
                {detalle.movimientos.length} registros
              </span>
            </div>
            <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
          </div>

          {/* Resumen del detalle */}
          {detalle.movimientos.length > 0 && (() => {
            const ing = detalle.movimientos.filter(m => m.tipomovimiento === 'ingreso');
            const egr = detalle.movimientos.filter(m => m.tipomovimiento === 'egreso');
            const totalIng = ing.reduce((s, m) => s + m.monto, 0);
            const totalEgr = egr.reduce((s, m) => s + m.monto, 0);
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: 16, background: C.crema, borderBottom: `1px solid ${C.borde}` }}>
                {[
                  { label: 'SALDO INICIAL', value: fmt2(detalle.apertura.saldoInicial), color: C.textoSub },
                  { label: 'INGRESOS',      value: fmt2(totalIng),  color: C.verde },
                  { label: 'EGRESOS',       value: fmt2(totalEgr),  color: C.error },
                  { label: 'SALDO FINAL',   value: fmt2(detalle.apertura.saldoInicial + totalIng - totalEgr), color: C.rojo },
                ].map(s => (
                  <div key={s.label} style={{ background: C.blanco, borderRadius: 10, padding: '10px 14px', border: `1px solid ${s.color}33` }}>
                    <div style={{ fontSize: 9, color: s.color, fontWeight: 800, letterSpacing: 1 }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.value}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.cremaDark }}>
                  {['Hora', 'Tipo', 'M. Pago', 'Descripción', 'Ref.', '💵 Efectivo', '📱 Transf.', 'Total'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', color: C.rojo, fontWeight: 800, fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detalle.movimientos.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: C.textoSub }}>Sin movimientos</td></tr>
                )}
                {detalle.movimientos.map((m, i) => (
                  <tr key={m._id} style={{ background: i % 2 === 0 ? C.blanco : C.crema, borderBottom: `1px solid ${C.borde}` }}>
                    <td style={{ padding: '8px 14px', fontSize: 12, color: C.textoSub, whiteSpace: 'nowrap' }}>{fmtT(m.fecha)}</td>
                    <td style={{ padding: '8px 14px' }}>
                      <Badge color={m.tipomovimiento === 'ingreso' ? C.verde : C.error}>
                        {m.tipomovimiento === 'ingreso' ? '↑ Ingreso' : '↓ Egreso'}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 14px' }}>
                      <Badge color={m.mediodepago === 'transferencia' ? C.azul : C.verde}>
                        {m.mediodepago === 'transferencia' ? '📱' : '💵'} {m.mediodepago || 'efectivo'}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: 13 }}>{m.descmovimiento}</td>
                    <td style={{ padding: '8px 14px', fontSize: 12, color: C.textoSub }}>{m.tiporef || '—'}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 700, color: C.verde, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {m.efectivo > 0 ? fmt2(m.efectivo) : '—'}
                    </td>
                    <td style={{ padding: '8px 14px', fontWeight: 700, color: C.azul, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {m.transferencia > 0 ? fmt2(m.transferencia) : '—'}
                    </td>
                    <td style={{ padding: '8px 14px', fontWeight: 900, color: m.tipomovimiento === 'ingreso' ? C.verde : C.error, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {m.tipomovimiento === 'egreso' ? '−' : '+'}{fmt2(m.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal abrir caja */}
      {modalAb && (
        <Modal title="💰 Abrir Caja del Día" onClose={() => setModalAb(false)}>
          <div style={{ background: C.amarilloBg, borderRadius: 10, padding: '12px 14px', marginBottom: 16, border: `1px solid ${C.bordeOsc}44`, fontSize: 13, color: C.rojo, fontWeight: 600 }}>
            Ingresá el dinero disponible al iniciar el día, separado por medio de pago
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={labelSt}>💵 EFECTIVO INICIAL ($)</span>
              <input style={inputSt} type="number" value={formAb.efectivoInicial} onChange={e => setFormAb(f => ({ ...f, efectivoInicial: e.target.value }))} placeholder="0" autoFocus />
            </div>
            <div>
              <span style={labelSt}>📱 TRANSFERENCIA INICIAL ($)</span>
              <input style={inputSt} type="number" value={formAb.transferenciaInicial} onChange={e => setFormAb(f => ({ ...f, transferenciaInicial: e.target.value }))} placeholder="0" />
            </div>
          </div>
          {((+formAb.efectivoInicial || 0) + (+formAb.transferenciaInicial || 0)) > 0 && (
            <div style={{ background: C.verdeBg, borderRadius: 8, padding: '8px 14px', marginBottom: 12, color: C.verde, fontWeight: 800 }}>
              Total inicial: {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format((+formAb.efectivoInicial || 0) + (+formAb.transferenciaInicial || 0))}
            </div>
          )}
          <span style={labelSt}>OBSERVACIONES (opcional)</span>
          <input style={inputSt} value={formAb.observaciones} onChange={e => setFormAb(f => ({ ...f, observaciones: e.target.value }))} placeholder="Ej: Apertura normal" />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModalAb(false)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={abrirCaja} disabled={loading} color={C.verde} sm>{loading ? 'Abriendo...' : '💰 Abrir Caja'}</Btn>
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          msg={`¿Eliminar la apertura del ${fmtD(confirm.fecha)}? Solo se puede eliminar si no tiene movimientos asociados.`}
          onConfirm={() => eliminarApertura(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}


// ─── VENTA MANUAL (solo admin) ───────────────────────────────────────────────
function VentaManual({ showToast }) {
  const [productos,   setProductos]   = useState([]);
  const [aperturas,   setAperturas]   = useState([]);
  const [carrito,     setCarrito]     = useState([]);
  const [fecha,       setFecha]       = useState(() => {
    // Por defecto: ayer en Argentina
    const ar = new Date(Date.now() - 3*60*60*1000);
    ar.setDate(ar.getDate() - 1);
    return ar.toISOString().split('T')[0];
  });
  const [hora,        setHora]        = useState('12:00');
  const [aperturaid,  setAperturaid]  = useState('');
  const [mediodepago, setMediodepago] = useState('efectivo');
  const [obs,         setObs]         = useState('');
  const [loading,     setLoading]     = useState(false);
  const [buscar,      setBuscar]      = useState('');

  useEffect(() => {
    api.get('/productos/con-precio').then(d => setProductos(Array.isArray(d)?d:[]));
    api.get('/caja/aperturas?limit=30').then(d => setAperturas(Array.isArray(d)?d:[]));
  }, []);

  // Al cambiar fecha, pre-seleccionar la apertura de ese día si existe
  useEffect(() => {
    if (!fecha || !aperturas.length) return;
    const match = aperturas.find(a => {
      const d = new Date(a.fecha).toISOString().split('T')[0];
      return d === fecha;
    });
    setAperturaid(match ? match._id : '');
  }, [fecha, aperturas]);

  const fmt2 = n => new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(n||0);
  const filtrados = buscar ? productos.filter(p => p.productonombre.toLowerCase().includes(buscar.toLowerCase())) : productos;

  const agregar = (prod) => {
    if (!prod.precio) return showToast('Sin precio vigente', 'error');
    setCarrito(c => {
      const ex = c.find(i => i.productoid === prod._id);
      if (ex) return c.map(i => i.productoid===prod._id ? {...i, cantidad:+(i.cantidad+0.1).toFixed(1), total:+(i.preciovta*(i.cantidad+0.1)).toFixed(2)} : i);
      return [...c, { productoid:prod._id, productonombre:prod.productonombre, precioid:prod.precio._id, preciovta:prod.precio.preciovta, cantidad:1, total:prod.precio.preciovta }];
    });
  };

  const setCant = (pid, val) => {
    const n = parseFloat(val);
    if (isNaN(n)||n<0) return;
    if (n===0) return setCarrito(c=>c.filter(i=>i.productoid!==pid));
    setCarrito(c=>c.map(i=>i.productoid===pid?{...i,cantidad:n,total:+(n*i.preciovta).toFixed(2)}:i));
  };

  const setPF = (pid, val) => {
    const n = parseFloat(val); if(isNaN(n)) return;
    setCarrito(c=>c.map(i=>i.productoid===pid?{...i,precioFinal:n,total:n}:i));
  };

  const totalCarrito = carrito.reduce((s,i)=>s+(i.precioFinal||i.total),0);

  const confirmar = async () => {
    if (!carrito.length) return showToast('Carrito vacío','error');
    if (!fecha) return showToast('Seleccionar fecha','error');
    setLoading(true);
    const fechaHora = new Date(`${fecha}T${hora}:00`);
    // Ajustar a UTC desde Argentina (UTC-3)
    fechaHora.setTime(fechaHora.getTime() + 3*60*60*1000);
    const res = await api.post('/ventas/manual', {
      detalles: carrito, observaciones: obs, mediodepago,
      fecha: fechaHora.toISOString(),
      aperturaid: aperturaid || undefined,
    });
    setLoading(false);
    if (res._id) {
      showToast('✅ Venta manual registrada');
      setCarrito([]); setObs('');
    } else showToast(res.error||'Error','error');
  };

  const tipoColor2 = { entero:{bg:'#F5C200',text:'#7A1010'}, trozo:{bg:'#9B1515',text:'#fff'}, elaborado:{bg:'#6A1B9A',text:'#fff'}, menudencia:{bg:'#E65100',text:'#fff'}, otro:{bg:'#E8D8B0',text:'#3D0808'} };

  return (
    <div>
      <div style={{ background:'#FFF3CD', border:'1px solid #C8A800', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:13, color:'#7A5900' }}>
        ⚠️ <b>Venta Manual</b> — permite registrar ventas con fecha retroactiva sin control de caja abierta. Solo para correcciones administrativas.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:18 }}>
        {/* Fecha y hora */}
        <div style={{ background:C.blanco, borderRadius:12, padding:16, border:`1px solid ${C.borde}` }}>
          <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>📅 Fecha y hora de la venta</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <span style={labelSt}>FECHA</span>
              <input type="date" style={inputSt} value={fecha} onChange={e=>setFecha(e.target.value)} />
            </div>
            <div>
              <span style={labelSt}>HORA</span>
              <input type="time" style={inputSt} value={hora} onChange={e=>setHora(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Caja */}
        <div style={{ background:C.blanco, borderRadius:12, padding:16, border:`1px solid ${C.borde}` }}>
          <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>💰 Caja a impactar</div>
          <span style={labelSt}>APERTURA DE CAJA (opcional)</span>
          <select style={inputSt} value={aperturaid} onChange={e=>setAperturaid(e.target.value)}>
            <option value="">Sin impacto en caja</option>
            {aperturas.map(a => {
              const fechaStr = new Date(a.fecha).toLocaleDateString('es-AR',{timeZone:'UTC'});
              const horaStr  = new Date(a.fechaApertura).toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'});
              return <option key={a._id} value={a._id}>{fechaStr} — {horaStr} ({a.estado}) ${(a.saldoInicial||0).toLocaleString()}</option>;
            })}
          </select>
          <span style={labelSt}>MEDIO DE PAGO</span>
          <div style={{ display:'flex', gap:8 }}>
            {[['efectivo','💵 Efectivo',C.verde],['transferencia','📱 Transferencia',C.azul]].map(([v,l,c])=>(
              <button key={v} onClick={()=>setMediodepago(v)} style={{ flex:1, padding:'9px', borderRadius:8, border:`2px solid ${mediodepago===v?c:C.borde}`, background:mediodepago===v?c+'18':C.blanco, color:mediodepago===v?c:C.textoSub, fontWeight:700, fontSize:13 }}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:16 }}>
        {/* Productos */}
        <div style={{ background:C.blanco, borderRadius:12, padding:16, border:`1px solid ${C.borde}` }}>
          <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>🥩 Seleccionar productos</div>
          <input style={{ ...inputSt, marginBottom:12 }} placeholder="🔍 Buscar producto..." value={buscar} onChange={e=>setBuscar(e.target.value)} />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:8, maxHeight:360, overflowY:'auto' }}>
            {filtrados.map(p => {
              const tc = tipoColor2[p.productotipo] || tipoColor2.otro;
              const enC = carrito.find(i=>i.productoid===p._id);
              return (
                <button key={p._id} onClick={()=>agregar(p)} style={{ background:enC?C.rojo+'12':C.crema, border:`2px solid ${enC?C.rojo:C.borde}`, borderRadius:10, padding:'10px', textAlign:'left', cursor:p.precio?'pointer':'not-allowed', opacity:p.precio?1:0.5, position:'relative' }}>
                  {enC&&<div style={{position:'absolute',top:6,right:6,background:C.rojo,color:'#fff',borderRadius:'50%',width:20,height:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:900}}>{enC.cantidad}</div>}
                  <div style={{fontWeight:700,fontSize:12,color:C.textoOsc,marginBottom:3,lineHeight:1.2}}>{p.productonombre}</div>
                  <span style={{background:tc.bg,color:tc.text,borderRadius:5,padding:'1px 6px',fontSize:10,fontWeight:800}}>{p.productotipo}</span>
                  <div style={{marginTop:6,fontWeight:900,fontSize:14,color:C.rojo}}>{p.precio?fmt2(p.precio.preciovta):<span style={{color:C.error,fontSize:11}}>Sin precio</span>}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Carrito */}
        <div style={{ background:C.blanco, borderRadius:12, padding:16, border:`1px solid ${C.borde}`, display:'flex', flexDirection:'column' }}>
          <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>🛒 Carrito</div>
          <div style={{ flex:1, overflowY:'auto', marginBottom:12 }}>
            {carrito.length===0
              ? <div style={{textAlign:'center',color:C.textoSub,padding:'32px 0'}}>Seleccioná productos</div>
              : carrito.map(item=>(
                <div key={item.productoid} style={{background:C.crema,borderRadius:8,padding:'10px',marginBottom:8,border:`1px solid ${C.borde}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                    <div style={{fontWeight:700,fontSize:13,color:C.textoOsc}}>{item.productonombre}</div>
                    <button onClick={()=>setCarrito(c=>c.filter(i=>i.productoid!==item.productoid))} style={{background:'none',border:'none',color:C.error,fontSize:16,cursor:'pointer'}}>×</button>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                    <button onClick={()=>setCant(item.productoid,+(item.cantidad-0.1).toFixed(1))} style={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:5,color:C.rojo,width:26,height:26,fontSize:14,fontWeight:900}}>−</button>
                    <input type="number" value={item.cantidad} min="0.01" step="0.1" onChange={e=>setCant(item.productoid,e.target.value)} style={{width:48,textAlign:'center',border:`1px solid ${C.borde}`,borderRadius:5,padding:'3px',fontSize:13}} />
                    <button onClick={()=>setCant(item.productoid,+(item.cantidad+0.1).toFixed(1))} style={{background:C.blanco,border:`1px solid ${C.borde}`,borderRadius:5,color:C.verde,width:26,height:26,fontSize:14,fontWeight:900}}>+</button>
                    <span style={{fontSize:11,color:C.textoSub}}>{fmt2(item.preciovta)}/u</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:11,color:C.textoSub,width:60}}>Final:</span>
                    <input type="number" value={item.precioFinal||item.total} onChange={e=>setPF(item.productoid,e.target.value)} style={{flex:1,border:`1.5px solid ${C.amarilloOsc||'#C8A800'}`,borderRadius:6,padding:'4px 8px',fontSize:14,fontWeight:800,color:C.rojo,textAlign:'right'}} />
                  </div>
                  {(item.precioFinal&&Math.abs(item.precioFinal-item.total)>0.01)&&(
                    <div style={{fontSize:10,color:'#C8A800',marginTop:2}}>calc: {fmt2(item.total)}</div>
                  )}
                </div>
              ))
            }
          </div>
          {carrito.length>0&&(
            <>
              <input style={{...inputSt,marginBottom:10}} placeholder="Observaciones..." value={obs} onChange={e=>setObs(e.target.value)} />
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'#FFF8E1',borderRadius:10,marginBottom:10}}>
                <span style={{fontWeight:900,color:C.rojo}}>TOTAL</span>
                <span style={{fontWeight:900,fontSize:20,color:C.rojo}}>{fmt2(totalCarrito)}</span>
              </div>
              <Btn onClick={confirmar} disabled={loading} color={C.rojo}>{loading?'Registrando...':'✓ Registrar Venta Manual'}</Btn>
              <button onClick={()=>setCarrito([])} style={{width:'100%',background:'none',border:`1px solid ${C.borde}`,borderRadius:8,padding:'7px',color:C.textoSub,marginTop:6,fontSize:13,cursor:'pointer'}}>🗑 Vaciar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── COMPRA MANUAL (solo admin) ──────────────────────────────────────────────
function CompraManual({ showToast }) {
  const [aperturas,   setAperturas]   = useState([]);
  const [fecha,       setFecha]       = useState(() => {
    const ar = new Date(Date.now() - 3*60*60*1000);
    ar.setDate(ar.getDate() - 1);
    return ar.toISOString().split('T')[0];
  });
  const [hora,        setHora]        = useState('12:00');
  const [aperturaid,  setAperturaid]  = useState('');
  const [mediodepago, setMediodepago] = useState('efectivo');
  const [monto,       setMonto]       = useState('');
  const [proveedor,   setProveedor]   = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [loading,     setLoading]     = useState(false);

  const fmt2 = n => new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(n||0);

  useEffect(() => {
    api.get('/caja/aperturas?limit=30').then(d => setAperturas(Array.isArray(d)?d:[]));
  }, []);

  useEffect(() => {
    if (!fecha || !aperturas.length) return;
    const match = aperturas.find(a => new Date(a.fecha).toISOString().split('T')[0] === fecha);
    setAperturaid(match ? match._id : '');
  }, [fecha, aperturas]);

  const confirmar = async () => {
    if (!monto || +monto <= 0) return showToast('Ingresar monto válido','error');
    if (!proveedor && !descripcion) return showToast('Ingresar proveedor o descripción','error');
    setLoading(true);
    const fechaHora = new Date(`${fecha}T${hora}:00`);
    fechaHora.setTime(fechaHora.getTime() + 3*60*60*1000);
    const res = await api.post('/compras/manual', {
      monto: +monto, proveedor, descripcion, mediodepago,
      fecha: fechaHora.toISOString(),
      aperturaid: aperturaid || undefined,
    });
    setLoading(false);
    if (res._id) {
      showToast('✅ Compra manual registrada');
      setMonto(''); setProveedor(''); setDescripcion('');
    } else showToast(res.error||'Error','error');
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ background:'#FFF3CD', border:'1px solid #C8A800', borderRadius:10, padding:'10px 16px', marginBottom:18, fontSize:13, color:'#7A5900' }}>
        ⚠️ <b>Compra Manual</b> — permite registrar compras con fecha retroactiva sin control de caja abierta. Solo para correcciones administrativas.
      </div>

      <div style={{ background:C.blanco, borderRadius:12, padding:20, border:`1px solid ${C.borde}`, display:'flex', flexDirection:'column', gap:0 }}>

        {/* Fecha y hora */}
        <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>📅 Fecha y hora</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:4 }}>
          <div>
            <span style={labelSt}>FECHA</span>
            <input type="date" style={inputSt} value={fecha} onChange={e=>setFecha(e.target.value)} />
          </div>
          <div>
            <span style={labelSt}>HORA</span>
            <input type="time" style={inputSt} value={hora} onChange={e=>setHora(e.target.value)} />
          </div>
        </div>

        {/* Datos de la compra */}
        <div style={{ fontWeight:800, color:C.rojo, marginBottom:12, marginTop:8 }}>📦 Datos de la compra</div>
        <span style={labelSt}>MONTO ($)</span>
        <input style={inputSt} type="number" value={monto} onChange={e=>setMonto(e.target.value)} placeholder="0" autoFocus />
        <span style={labelSt}>PROVEEDOR</span>
        <input style={inputSt} value={proveedor} onChange={e=>setProveedor(e.target.value)} placeholder="Nombre del proveedor" />
        <span style={labelSt}>DESCRIPCIÓN</span>
        <input style={inputSt} value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Detalle de la compra" />

        {/* Medio de pago */}
        <span style={labelSt}>MEDIO DE PAGO</span>
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {[['efectivo','💵 Efectivo',C.verde],['transferencia','📱 Transferencia',C.azul]].map(([v,l,c])=>(
            <button key={v} onClick={()=>setMediodepago(v)} style={{ flex:1, padding:'10px', borderRadius:8, border:`2px solid ${mediodepago===v?c:C.borde}`, background:mediodepago===v?c+'18':C.blanco, color:mediodepago===v?c:C.textoSub, fontWeight:700, fontSize:13, cursor:'pointer' }}>{l}</button>
          ))}
        </div>

        {/* Caja */}
        <div style={{ fontWeight:800, color:C.rojo, marginBottom:12 }}>💰 Caja a impactar</div>
        <span style={labelSt}>APERTURA DE CAJA (opcional)</span>
        <select style={inputSt} value={aperturaid} onChange={e=>setAperturaid(e.target.value)}>
          <option value="">Sin impacto en caja</option>
          {aperturas.map(a => {
            const fechaStr = new Date(a.fecha).toLocaleDateString('es-AR',{timeZone:'UTC'});
            const horaStr  = new Date(a.fechaApertura).toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'});
            return <option key={a._id} value={a._id}>{fechaStr} — {horaStr} ({a.estado}) ${(a.saldoInicial||0).toLocaleString()}</option>;
          })}
        </select>

        {/* Preview */}
        {monto>0&&(
          <div style={{ background:'#FFF3CD', borderRadius:10, padding:'12px 16px', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:12, color:'#7A5900', fontWeight:700 }}>EGRESO A REGISTRAR</div>
              <div style={{ fontSize:13, color:'#3D0808' }}>{proveedor||'Sin proveedor'} — {descripcion||'Sin descripción'}</div>
              <div style={{ fontSize:11, color:'#7A5900' }}>{fecha} {hora} · {mediodepago}</div>
            </div>
            <div style={{ fontSize:22, fontWeight:900, color:C.error }}>{fmt2(+monto)}</div>
          </div>
        )}

        <Btn onClick={confirmar} disabled={loading} color={C.error}>{loading?'Registrando...':'✓ Registrar Compra Manual'}</Btn>
      </div>
    </div>
  );
}

// ─── RETIROS DE SOCIOS ────────────────────────────────────────────────────────
function CRUDRetiros({ showToast }) {
  const [retiros, setRetiros]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [totales, setTotales]   = useState({ monto: 0, efectivo: 0, transferencia: 0 });
  const [socios, setSocios]     = useState([]);
  const [aperturas, setAperturas] = useState([]);
  const [modal, setModal]       = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(null);
  const [loading, setLoading]   = useState(false);

  // Filtros
  const [desde, setDesde]   = useState('');
  const [hasta, setHasta]   = useState('');
  const [socioFiltro, setSocioFiltro] = useState('todos');

  // Formulario de nuevo retiro
  const [fecha, setFecha]       = useState(() => new Date().toISOString().split('T')[0]);
  const [hora, setHora]         = useState(() => new Date().toTimeString().slice(0,5));
  const [usuarioid, setUsuarioid] = useState('');
  const [monto, setMonto]       = useState('');
  const [mediodepago, setMediodepago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [aperturaid, setAperturaid] = useState('');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    if (socioFiltro !== 'todos') params.set('usuario', socioFiltro);
    api.get(`/retiros?${params.toString()}`).then(d => {
      const lista = Array.isArray(d?.retiros) ? d.retiros : [];
      setRetiros(lista);
      setTotal(d?.total || 0);
      // Totales del backend (cubren todo el filtro); si no vienen, se calculan sobre lo visible
      setTotales(d?.totales || calcTotales(lista));
    });
  }, [desde, hasta, socioFiltro]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/retiros/socios').then(d => setSocios(Array.isArray(d) ? d : []));
    api.get('/caja/aperturas?limit=30').then(d => setAperturas(Array.isArray(d) ? d : []));
  }, []);

  const abrirNuevo = () => {
    setFecha(new Date().toISOString().split('T')[0]);
    setHora(new Date().toTimeString().slice(0,5));
    setUsuarioid(''); setMonto(''); setMediodepago('efectivo'); setObservaciones(''); setAperturaid('');
    setModal(true);
  };

  const registrar = async () => {
    if (!usuarioid) return showToast('Seleccionar el socio que retira', 'error');
    if (!monto || +monto <= 0) return showToast('Ingresar un monto válido', 'error');
    setLoading(true);
    try {
      const fechaHora = new Date(`${fecha}T${hora}:00`);
      fechaHora.setTime(fechaHora.getTime() + 3*60*60*1000); // ajuste AR → UTC, igual que Venta/Compra Manual
      const res = await api.post('/retiros', {
        usuarioid, monto: +monto, mediodepago, observaciones,
        fecha: fechaHora.toISOString(),
        aperturaid: aperturaid || undefined,
      });
      if (res._id) { showToast('💸 Retiro registrado'); setModal(false); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const anular = async (r) => {
    try {
      await api.patch(`/retiros/${r._id}/anular`, {});
      showToast('Retiro anulado'); setConfirmAnular(null); load();
    } catch { showToast('Error al anular', 'error'); }
  };

  return (
    <div>
      <SectionHeader title="Retiros de Socios" count={total} onAdd={abrirNuevo} addLabel="+ Nuevo Retiro" />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Socio</span>
          <select style={{ ...inputSt, marginBottom: 0, width: 180 }} value={socioFiltro} onChange={e => setSocioFiltro(e.target.value)}>
            <option value="todos">Todos los socios</option>
            {socios.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
          </select>
        </div>
        <Btn onClick={() => { setDesde(''); setHasta(''); setSocioFiltro('todos'); }} outline color={C.gris} sm>Limpiar</Btn>
      </div>

      <ResumenTotales cantidad={total} visibles={retiros.length} totales={totales} singular="retiro" plural="retiros" signo="−" colorTotal={C.error} />

      <Table headers={['Fecha', 'Socio', 'Medio de pago', 'Observaciones', 'Monto', 'Acciones']} empty="Sin retiros registrados">
        {retiros.map((r, i) => (
          <tr key={r._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema, opacity: r.anulado ? 0.5 : 1 }}>
            <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(r.fecha)}</td>
            <td style={{ ...tdSt, fontWeight: 700 }}>🤝 {r.usuario}</td>
            <td style={tdSt}>
              <span style={{ background: r.mediodepago === 'transferencia' ? C.azulBg : C.verdeBg, color: r.mediodepago === 'transferencia' ? C.azul : C.verde, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                {r.mediodepago === 'transferencia' ? '📱 Trans.' : '💵 Efect.'}
              </span>
            </td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub }}>{r.observaciones || '—'}{r.anulado && <b style={{ color: C.error }}> (anulado)</b>}</td>
            <td style={{ ...tdSt, fontWeight: 800, color: C.error, textAlign: 'right', whiteSpace: 'nowrap' }}>−{fmt(r.monto)}</td>
            <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
              {!r.anulado && (
                <button onClick={() => setConfirmAnular(r)} style={{ background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                  Anular
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title="💸 Nuevo Retiro de Socio" onClose={() => setModal(false)}>
          <div style={{ background: '#FDEAEA', border: `1px solid ${C.error}55`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#7A1010' }}>
            ⚠️ El egreso se registrará en caja aunque esté cerrada.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={labelSt}>FECHA</span>
              <input type="date" style={inputSt} value={fecha} onChange={e => setFecha(e.target.value)} />
            </div>
            <div>
              <span style={labelSt}>HORA</span>
              <input type="time" style={inputSt} value={hora} onChange={e => setHora(e.target.value)} />
            </div>
          </div>

          <span style={labelSt}>SOCIO QUE RETIRA</span>
          {socios.length === 0 ? (
            <div style={{ background: '#FFF3CD', border: '1px solid #C8A800', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: '#7A5900' }}>
              ⚠️ No hay usuarios con categoría "Socio". Creá uno en Usuarios primero.
            </div>
          ) : (
            <select style={inputSt} value={usuarioid} onChange={e => setUsuarioid(e.target.value)}>
              <option value="">Seleccionar socio...</option>
              {socios.map(s => <option key={s._id} value={s._id}>{s.nombre}</option>)}
            </select>
          )}

          <span style={labelSt}>MONTO ($)</span>
          <input style={inputSt} type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0" />

          <span style={labelSt}>MEDIO DE PAGO</span>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['efectivo','💵 Efectivo',C.verde],['transferencia','📱 Transferencia',C.azul]].map(([v,l,c]) => (
              <button key={v} onClick={() => setMediodepago(v)} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${mediodepago === v ? c : C.borde}`, background: mediodepago === v ? c+'18' : C.blanco, color: mediodepago === v ? c : C.textoSub, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>

          <span style={labelSt}>OBSERVACIONES</span>
          <input style={inputSt} value={observaciones} onChange={e => setObservaciones(e.target.value)} placeholder="Motivo del retiro (opcional)" />

          <span style={labelSt}>CAJA A IMPACTAR (opcional)</span>
          <select style={inputSt} value={aperturaid} onChange={e => setAperturaid(e.target.value)}>
            <option value="">Caja del día automática</option>
            {aperturas.map(a => {
              const fechaStr = new Date(a.fecha).toLocaleDateString('es-AR', { timeZone: 'UTC' });
              const horaStr  = new Date(a.fechaApertura).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
              return <option key={a._id} value={a._id}>{fechaStr} — {horaStr} ({a.estado}) ${(a.saldoInicial||0).toLocaleString()}</option>;
            })}
          </select>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModal(false)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={registrar} disabled={loading || socios.length === 0} color={C.error} sm>{loading ? 'Registrando...' : '💸 Registrar Retiro'}</Btn>
          </div>
        </Modal>
      )}

      {confirmAnular && (
        <ConfirmModal
          msg={`¿Anular el retiro de ${fmt(confirmAnular.monto)} de ${confirmAnular.usuario}? Esto también elimina el movimiento de egreso en caja.`}
          onConfirm={() => anular(confirmAnular)}
          onCancel={() => setConfirmAnular(null)}
        />
      )}
    </div>
  );
}

// ─── INGRESOS DE SOCIOS ───────────────────────────────────────────────────────
function CRUDIngresosSocios({ showToast }) {
  const [ingresos, setIngresos] = useState([]);
  const [total, setTotal]       = useState(0);
  const [totales, setTotales]   = useState({ monto: 0, efectivo: 0, transferencia: 0 });
  const [socios, setSocios]     = useState([]);
  const [aperturas, setAperturas] = useState([]);
  const [modal, setModal]       = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(null);
  const [loading, setLoading]   = useState(false);

  // Filtros
  const [desde, setDesde]   = useState('');
  const [hasta, setHasta]   = useState('');
  const [socioFiltro, setSocioFiltro] = useState('todos');

  // Formulario de nuevo ingreso
  const [fecha, setFecha]       = useState(() => new Date().toISOString().split('T')[0]);
  const [hora, setHora]         = useState(() => new Date().toTimeString().slice(0,5));
  const [usuarioid, setUsuarioid] = useState('');
  const [monto, setMonto]       = useState('');
  const [mediodepago, setMediodepago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [aperturaid, setAperturaid] = useState('');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    if (socioFiltro !== 'todos') params.set('usuario', socioFiltro);
    api.get(`/ingresos-socios?${params.toString()}`).then(d => {
      const lista = Array.isArray(d?.ingresos) ? d.ingresos : [];
      setIngresos(lista);
      setTotal(d?.total || 0);
      // Totales del backend (cubren todo el filtro); si no vienen, se calculan sobre lo visible
      setTotales(d?.totales || calcTotales(lista));
    });
  }, [desde, hasta, socioFiltro]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/ingresos-socios/socios').then(d => setSocios(Array.isArray(d) ? d : []));
    api.get('/caja/aperturas?limit=30').then(d => setAperturas(Array.isArray(d) ? d : []));
  }, []);

  const abrirNuevo = () => {
    setFecha(new Date().toISOString().split('T')[0]);
    setHora(new Date().toTimeString().slice(0,5));
    setUsuarioid(''); setMonto(''); setMediodepago('efectivo'); setObservaciones(''); setAperturaid('');
    setModal(true);
  };

  const registrar = async () => {
    if (!usuarioid) return showToast('Seleccionar el socio que aporta', 'error');
    if (!monto || +monto <= 0) return showToast('Ingresar un monto válido', 'error');
    setLoading(true);
    try {
      const fechaHora = new Date(`${fecha}T${hora}:00`);
      fechaHora.setTime(fechaHora.getTime() + 3*60*60*1000); // ajuste AR → UTC, igual que Retiros/Venta/Compra Manual
      const res = await api.post('/ingresos-socios', {
        usuarioid, monto: +monto, mediodepago, observaciones,
        fecha: fechaHora.toISOString(),
        aperturaid: aperturaid || undefined,
      });
      if (res._id) { showToast('💰 Ingreso registrado'); setModal(false); load(); }
      else showToast(res.error || 'Error', 'error');
    } catch { showToast('Error de conexión', 'error'); }
    setLoading(false);
  };

  const anular = async (r) => {
    try {
      await api.patch(`/ingresos-socios/${r._id}/anular`, {});
      showToast('Ingreso anulado'); setConfirmAnular(null); load();
    } catch { showToast('Error al anular', 'error'); }
  };

  return (
    <div>
      <SectionHeader title="Ingresos de Socios" count={total} onAdd={abrirNuevo} addLabel="+ Nuevo Ingreso" />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Desde</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Hasta</span>
          <input type="date" style={{ ...inputSt, marginBottom: 0, width: 150 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: C.textoSub, fontWeight: 600 }}>Socio</span>
          <select style={{ ...inputSt, marginBottom: 0, width: 180 }} value={socioFiltro} onChange={e => setSocioFiltro(e.target.value)}>
            <option value="todos">Todos los socios</option>
            {socios.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
          </select>
        </div>
        <Btn onClick={() => { setDesde(''); setHasta(''); setSocioFiltro('todos'); }} outline color={C.gris} sm>Limpiar</Btn>
      </div>

      <ResumenTotales cantidad={total} visibles={ingresos.length} totales={totales} singular="ingreso" plural="ingresos" signo="+" colorTotal={C.verde} />

      <Table headers={['Fecha', 'Socio', 'Medio de pago', 'Observaciones', 'Monto', 'Acciones']} empty="Sin ingresos registrados">
        {ingresos.map((r, i) => (
          <tr key={r._id} className="admin-row" style={{ background: i % 2 === 0 ? C.blanco : C.crema, opacity: r.anulado ? 0.5 : 1 }}>
            <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(r.fecha)}</td>
            <td style={{ ...tdSt, fontWeight: 700 }}>🤝 {r.usuario}</td>
            <td style={tdSt}>
              <span style={{ background: r.mediodepago === 'transferencia' ? C.azulBg : C.verdeBg, color: r.mediodepago === 'transferencia' ? C.azul : C.verde, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                {r.mediodepago === 'transferencia' ? '📱 Trans.' : '💵 Efect.'}
              </span>
            </td>
            <td style={{ ...tdSt, fontSize: 12, color: C.textoSub }}>{r.observaciones || '—'}{r.anulado && <b style={{ color: C.error }}> (anulado)</b>}</td>
            <td style={{ ...tdSt, fontWeight: 800, color: C.verde, textAlign: 'right', whiteSpace: 'nowrap' }}>+{fmt(r.monto)}</td>
            <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
              {!r.anulado && (
                <button onClick={() => setConfirmAnular(r)} style={{ background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>
                  Anular
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>

      {modal && (
        <Modal title="💰 Nuevo Ingreso de Socio" onClose={() => setModal(false)}>
          <div style={{ background: C.verdeBg, border: `1px solid ${C.verde}55`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#0F4A1C' }}>
            ⚠️ El ingreso se registrará en caja aunque esté cerrada.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={labelSt}>FECHA</span>
              <input type="date" style={inputSt} value={fecha} onChange={e => setFecha(e.target.value)} />
            </div>
            <div>
              <span style={labelSt}>HORA</span>
              <input type="time" style={inputSt} value={hora} onChange={e => setHora(e.target.value)} />
            </div>
          </div>

          <span style={labelSt}>SOCIO QUE APORTA</span>
          {socios.length === 0 ? (
            <div style={{ background: '#FFF3CD', border: '1px solid #C8A800', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: '#7A5900' }}>
              ⚠️ No hay usuarios con categoría "Socio". Creá uno en Usuarios primero.
            </div>
          ) : (
            <select style={inputSt} value={usuarioid} onChange={e => setUsuarioid(e.target.value)}>
              <option value="">Seleccionar socio...</option>
              {socios.map(s => <option key={s._id} value={s._id}>{s.nombre}</option>)}
            </select>
          )}

          <span style={labelSt}>MONTO ($)</span>
          <input style={inputSt} type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="0" />

          <span style={labelSt}>MEDIO DE PAGO</span>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['efectivo','💵 Efectivo',C.verde],['transferencia','📱 Transferencia',C.azul]].map(([v,l,c]) => (
              <button key={v} onClick={() => setMediodepago(v)} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${mediodepago === v ? c : C.borde}`, background: mediodepago === v ? c+'18' : C.blanco, color: mediodepago === v ? c : C.textoSub, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>

          <span style={labelSt}>OBSERVACIONES</span>
          <input style={inputSt} value={observaciones} onChange={e => setObservaciones(e.target.value)} placeholder="Motivo del ingreso (opcional)" />

          <span style={labelSt}>CAJA A IMPACTAR (opcional)</span>
          <select style={inputSt} value={aperturaid} onChange={e => setAperturaid(e.target.value)}>
            <option value="">Caja del día automática</option>
            {aperturas.map(a => {
              const fechaStr = new Date(a.fecha).toLocaleDateString('es-AR', { timeZone: 'UTC' });
              const horaStr  = new Date(a.fechaApertura).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
              return <option key={a._id} value={a._id}>{fechaStr} — {horaStr} ({a.estado}) ${(a.saldoInicial||0).toLocaleString()}</option>;
            })}
          </select>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModal(false)} outline color={C.gris} sm>Cancelar</Btn>
            <Btn onClick={registrar} disabled={loading || socios.length === 0} color={C.verde} sm>{loading ? 'Registrando...' : '💰 Registrar Ingreso'}</Btn>
          </div>
        </Modal>
      )}

      {confirmAnular && (
        <ConfirmModal
          msg={`¿Anular el ingreso de ${fmt(confirmAnular.monto)} de ${confirmAnular.usuario}? Esto también elimina el movimiento de ingreso en caja.`}
          onConfirm={() => anular(confirmAnular)}
          onCancel={() => setConfirmAnular(null)}
        />
      )}
    </div>
  );
}

const SECCIONES = [
  { key: 'productos', label: 'Productos',  icon: '🥩' },
  { key: 'precios',   label: 'Precios',    icon: '🏷️' },
  { key: 'ventas',    label: 'Ventas',     icon: '🛒' },
  { key: 'venta-manual', label: 'Venta Manual', icon: '✏️' },
  { key: 'compras',   label: 'Compras',    icon: '📦' },
  { key: 'compra-manual', label: 'Compra Manual', icon: '✏️' },
  { key: 'caja',      label: 'Caja',       icon: '💰' },
  { key: 'aperturas',  label: 'Aperturas',   icon: '📅' },
  { key: 'retiros',   label: 'Retiros',    icon: '💸' },
  { key: 'ingresos-socios', label: 'Ingresos Socios', icon: '💰' },
  { key: 'categorias',label: 'Categorías', icon: '🏷️' },
  { key: 'usuarios',  label: 'Usuarios',   icon: '👥' },
];

export default function AdminPanel() {
  const [user, setUser]       = useState(null);
  const [seccion, setSeccion] = useState('productos');
  const [toast, setToast]     = useState(null);
  const [sideOpen, setSideOpen] = useState(true);

  useEffect(() => {
    const token  = localStorage.getItem('token');
    const nombre = localStorage.getItem('userName');
    if (!token) { window.location.href = '/'; return; }
    setUser({ token, nombre });
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userName_raw');
    window.location.href = '/';
  };

  const showToast = (msg, type = 'ok') => setToast({ msg, type });

  if (!user) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Cargando...</div>;

  return (
    <>
      <style>{adminCSS}</style>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{ width: sideOpen ? 220 : 60, background: C.rojo, display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'width .2s', overflow: 'hidden' }}>
          {/* Logo */}
          <div style={{ padding: '16px 14px', borderBottom: `1px solid #ffffff22`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>🐓</span>
            {sideOpen && <div>
              <div style={{ fontWeight: 900, color: C.amarillo, fontSize: 15, lineHeight: 1 }}>De Pollo</div>
              <div style={{ fontSize: 10, color: '#ffffff88', marginTop: 2 }}>Admin Panel</div>
            </div>}
            <button onClick={() => setSideOpen(s => !s)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ffffff88', fontSize: 18, flexShrink: 0 }}>
              {sideOpen ? '◀' : '▶'}
            </button>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
            {SECCIONES.map(s => (
              <button key={s.key} onClick={() => setSeccion(s.key)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px', background: seccion === s.key ? '#ffffff22' : 'none',
                border: 'none', color: seccion === s.key ? C.amarillo : '#ffffff99',
                borderLeft: seccion === s.key ? `3px solid ${C.amarillo}` : '3px solid transparent',
                fontWeight: seccion === s.key ? 700 : 500, fontSize: 14, textAlign: 'left',
                whiteSpace: 'nowrap',
              }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{s.icon}</span>
                {sideOpen && s.label}
              </button>
            ))}
          </nav>

          {/* Footer sidebar */}
          <div style={{ padding: '12px 14px', borderTop: `1px solid #ffffff22` }}>
            {sideOpen && <div style={{ fontSize: 12, color: '#ffffff88', marginBottom: 8 }}>👤 {user.nombre}</div>}
            <button onClick={() => window.location.href = '/'} style={{ width: '100%', background: 'none', border: `1px solid #ffffff44`, borderRadius: 8, padding: '8px', color: '#ffffffcc', fontSize: 12, fontWeight: 600, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {sideOpen ? '← Volver al sistema' : '←'}
            </button>
            <button onClick={logout} style={{ width: '100%', background: 'none', border: `1px solid #ffffff44`, borderRadius: 8, padding: '8px', color: '#ffaaaa', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {sideOpen ? '🚪 Cerrar sesión' : '🚪'}
            </button>
          </div>
        </div>

        {/* Contenido principal */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Topbar */}
          <div style={{ background: C.blanco, borderBottom: `1px solid ${C.borde}`, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, boxShadow: '0 1px 4px #0001' }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.rojo }}>{SECCIONES.find(s => s.key === seccion)?.icon} {SECCIONES.find(s => s.key === seccion)?.label}</h1>
              <div style={{ fontSize: 12, color: C.textoSub, marginTop: 2 }}>Panel de Administración · De Pollo</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: C.textoSub, fontWeight: 600 }}>👤 {user.nombre}</span>
              <Btn onClick={logout} outline color={C.error} sm>Salir</Btn>
            </div>
          </div>

          {/* Sección activa */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            {seccion === 'productos' && <CRUDProductos showToast={showToast} />}
            {seccion === 'precios'   && <CRUDPrecios   showToast={showToast} />}
            {seccion === 'ventas'    && <CRUDVentas    showToast={showToast} />}
            {seccion === 'venta-manual' && <VentaManual   showToast={showToast} />}
            {seccion === 'compras'   && <CRUDCompras   showToast={showToast} />}
            {seccion === 'compra-manual' && <CompraManual  showToast={showToast} />}
            {seccion === 'caja'      && <CRUDCaja      showToast={showToast} />}
            {seccion === 'aperturas'  && <CRUDAperturas  showToast={showToast} />}
            {seccion === 'retiros'   && <CRUDRetiros    showToast={showToast} />}
            {seccion === 'ingresos-socios' && <CRUDIngresosSocios showToast={showToast} />}
            {seccion === 'categorias'&& <CRUDCategorias showToast={showToast} />}
            {seccion === 'usuarios'  && <CRUDUsuarios  showToast={showToast} />}
          </div>
        </div>
      </div>
    </>
  );
}
